import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface Challenge {
  id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  initialCode: string;
  solution: string;
  testCases: { input: any; expected: any }[];
  hints: string[];
}

const challenges: Challenge[] = [
  {
    id: 'palindrome',
    title: 'Palindrome Checker',
    description: 'Write a function that checks if a string is a palindrome (reads the same forwards and backwards).',
    difficulty: 'Easy',
    initialCode: `function isPalindrome(str) {
  // Your code here
  
}`,
    solution: `function isPalindrome(str) {
  const cleaned = str.toLowerCase().replace(/[^a-z0-9]/g, '');
  return cleaned === cleaned.split('').reverse().join('');
}`,
    testCases: [
      { input: 'racecar', expected: true },
      { input: 'hello', expected: false },
      { input: 'A man a plan a canal Panama', expected: true }
    ],
    hints: [
      'Consider removing spaces and converting to lowercase',
      'Compare the string with its reverse',
      'Use string methods like split(), reverse(), and join()'
    ]
  },
  {
    id: 'fibonacci',
    title: 'Fibonacci Sequence',
    description: 'Generate the nth number in the Fibonacci sequence efficiently.',
    difficulty: 'Medium',
    initialCode: `function fibonacci(n) {
  // Your code here
  
}`,
    solution: `function fibonacci(n) {
  if (n <= 1) return n;
  let a = 0, b = 1;
  for (let i = 2; i <= n; i++) {
    [a, b] = [b, a + b];
  }
  return b;
}`,
    testCases: [
      { input: 0, expected: 0 },
      { input: 1, expected: 1 },
      { input: 10, expected: 55 }
    ],
    hints: [
      'Consider iterative approach for efficiency',
      'Handle base cases (n = 0, n = 1)',
      'Use two variables to track previous values'
    ]
  },
  {
    id: 'array-flatten',
    title: 'Array Flattening',
    description: 'Flatten a nested array of arbitrary depth without using built-in flat() method.',
    difficulty: 'Hard',
    initialCode: `function flattenArray(arr) {
  // Your code here
  
}`,
    solution: `function flattenArray(arr) {
  const result = [];
  for (let item of arr) {
    if (Array.isArray(item)) {
      result.push(...flattenArray(item));
    } else {
      result.push(item);
    }
  }
  return result;
}`,
    testCases: [
      { input: [1, [2, 3], [4, [5, 6]]], expected: [1, 2, 3, 4, 5, 6] },
      { input: [[1, 2], [3, [4, [5]]]], expected: [1, 2, 3, 4, 5] },
      { input: [1, 2, 3], expected: [1, 2, 3] }
    ],
    hints: [
      'Use recursion to handle nested arrays',
      'Check if each item is an array using Array.isArray()',
      'Use spread operator to merge results'
    ]
  }
];

export default function CodePlayground() {
  const [selectedChallenge, setSelectedChallenge] = useState(challenges[0]);
  const [code, setCode] = useState(selectedChallenge.initialCode);
  const [output, setOutput] = useState('');
  const [testResults, setTestResults] = useState<{ passed: boolean; input: any; expected: any; actual: any }[]>([]);
  const [showHints, setShowHints] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  useEffect(() => {
    setCode(selectedChallenge.initialCode);
    setOutput('');
    setTestResults([]);
    setShowHints(false);
    setShowSolution(false);
  }, [selectedChallenge]);

  const runCode = async () => {
    setIsRunning(true);
    setOutput('');
    setTestResults([]);

    try {
      // Simulate execution delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));

      // Create a function from the code
      const func = new Function('return ' + code)();
      const results = [];

      for (const testCase of selectedChallenge.testCases) {
        try {
          const actual = func(testCase.input);
          const passed = JSON.stringify(actual) === JSON.stringify(testCase.expected);
          results.push({
            passed,
            input: testCase.input,
            expected: testCase.expected,
            actual
          });
        } catch (error: any) {
          results.push({
            passed: false,
            input: testCase.input,
            expected: testCase.expected,
            actual: `Error: ${error?.message || 'Unknown error'}`
          });
        }
      }

      setTestResults(results);
      const passedCount = results.filter(r => r.passed).length;
      setOutput(`Tests passed: ${passedCount}/${results.length}`);
    } catch (error: any) {
      setOutput(`Syntax Error: ${error?.message || 'Unknown error'}`);
    }

    setIsRunning(false);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-400 bg-green-400/10';
      case 'Medium': return 'text-yellow-400 bg-yellow-400/10';
      case 'Hard': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  return (
    <section id="playground" className="py-20 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Interactive <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Code Playground</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Test your coding skills with interactive challenges. Perfect for demonstrating problem-solving abilities to recruiters!
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 mx-auto rounded-full mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Challenge Selector */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-xl font-semibold text-white mb-4">Choose a Challenge</h3>
            {challenges.map((challenge) => (
              <motion.button
                key={challenge.id}
                onClick={() => setSelectedChallenge(challenge)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full p-4 rounded-xl border transition-all duration-300 text-left ${
                  selectedChallenge.id === challenge.id
                    ? 'border-purple-400 bg-purple-400/10'
                    : 'border-gray-600 bg-gray-800/50 hover:border-purple-400/50'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-white">{challenge.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(challenge.difficulty)}`}>
                    {challenge.difficulty}
                  </span>
                </div>
                <p className="text-gray-300 text-sm">{challenge.description}</p>
              </motion.button>
            ))}
          </motion.div>

          {/* Code Editor */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-gray-800/50 rounded-xl border border-gray-600 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-600">
                <h3 className="text-lg font-semibold text-white">{selectedChallenge.title}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowHints(!showHints)}
                    className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30 transition-colors"
                  >
                    💡 Hints
                  </button>
                  <button
                    onClick={() => setShowSolution(!showSolution)}
                    className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                  >
                    🔍 Solution
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                <p className="text-gray-300 mb-4">{selectedChallenge.description}</p>
                
                {showHints && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
                  >
                    <h4 className="text-yellow-400 font-semibold mb-2">💡 Hints:</h4>
                    <ul className="text-yellow-300 text-sm space-y-1">
                      {selectedChallenge.hints.map((hint, index) => (
                        <li key={index}>• {hint}</li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {showSolution && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
                  >
                    <h4 className="text-red-400 font-semibold mb-2">🔍 Solution:</h4>
                    <pre className="text-red-300 text-sm bg-gray-900/50 p-3 rounded overflow-x-auto">
                      <code>{selectedChallenge.solution}</code>
                    </pre>
                  </motion.div>
                )}
                
                <textarea
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full h-64 bg-gray-900/50 text-white font-mono text-sm p-4 rounded-lg border border-gray-600 focus:border-purple-400 focus:outline-none resize-none"
                  placeholder="Write your code here..."
                  spellCheck={false}
                />
                
                <div className="flex justify-between items-center mt-4">
                  <button
                    onClick={runCode}
                    disabled={isRunning}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRunning ? (
                      <>
                        <ClockIcon className="w-5 h-5 animate-spin" />
                        <span>Running...</span>
                      </>
                    ) : (
                      <>
                        <PlayIcon className="w-5 h-5" />
                        <span>Run Tests</span>
                      </>
                    )}
                  </button>
                  
                  {output && (
                    <div className="text-white font-medium">
                      {output}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Test Results */}
            {testResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800/50 rounded-xl border border-gray-600 p-4"
              >
                <h4 className="text-white font-semibold mb-4">Test Results</h4>
                <div className="space-y-3">
                  {testResults.map((result, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        result.passed
                          ? 'border-green-400/30 bg-green-400/10'
                          : 'border-red-400/30 bg-red-400/10'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        {result.passed ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-400" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-400" />
                        )}
                        <span className={result.passed ? 'text-green-400' : 'text-red-400'}>
                          Test {index + 1}: {result.passed ? 'PASSED' : 'FAILED'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-300 space-y-1">
                        <div><strong>Input:</strong> {JSON.stringify(result.input)}</div>
                        <div><strong>Expected:</strong> {JSON.stringify(result.expected)}</div>
                        <div><strong>Actual:</strong> {JSON.stringify(result.actual)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}